import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import { FiChevronDown, FiCheck, FiTrash2, FiUser, FiBell, FiCopy, FiExternalLink } from "react-icons/fi";
import api from "@/services/api";
// Apps are now managed via dedicated /app/new and /app/:appId pages
import useStore from "@/services/store";
import Loader from "@/components/loader";

const translations = {
  en: {
    stage_name: "Name",
    stage_description: "Description",
    stage_description_placeholder: "e.g. Live interview with guided scoring",
    treatment_type: "Treatment Type",
    automatic: "Automatic",
    manual: "Manual",
    responsible_service: "Responsible Service",
    auto_criteria: "Automatic Criteria",
    services: {
      hr: "Human Resources",
      tech: "Tech Team",
      management: "Management",
      direction: "Direction",
    },
    save_success: "Stage configuration saved",
    auto_check_exp: "Check years of experience",
    auto_check_skills: "Check required skills",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    delete_confirm: "Are you sure you want to delete this stage? This action cannot be undone.",
    stage_deleted: "Stage deleted successfully",
    error_deleting_stage: "Error deleting stage",
    attached_apps: "Application (optional)",
    app_optional_desc: "Select an app to automate candidate filtering and scoring at this stage.",
    no_app: "No Automation",
    no_app_desc: "Manual review only",
    app_config: "Configuration",
    stage_owner: "Owner",
    select_owner: "Select an owner",
    notify_owner: "Notify owner when a candidate arrives in this stage",
    owner_desc: "The person responsible for this stage",
    system: "System",
    share_title: "Public shortlist",
    share_desc: "Anyone with the link sees the candidates currently at this stage. No login. Names, roles and AI assessment only — contact info stays private.",
    share_on: "Public link enabled",
    share_off: "Sharing disabled",
    share_link: "Public link",
    share_copy: "Copy link",
    share_copied: "Link copied",
    share_open: "Open",
    share_failed: "Could not update sharing",
  },
  fr: {
    stage_name: "Nom",
    stage_description: "Description",
    stage_description_placeholder: "ex. Entretien live avec grille d'évaluation",
    treatment_type: "Type de traitement",
    automatic: "Automatique",
    manual: "Manuel",
    responsible_service: "Service responsable",
    auto_criteria: "Critères automatiques",
    services: {
      hr: "Ressources Humaines",
      tech: "Équipe Technique",
      management: "Management",
      direction: "Direction",
    },
    save_success: "Configuration de l'étape enregistrée",
    auto_check_exp: "Vérifier années d'expérience",
    auto_check_skills: "Vérifier compétences requises",
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    delete_confirm: "Êtes-vous sûr de vouloir supprimer cette étape ? Cette action est irréversible.",
    stage_deleted: "Étape supprimée avec succès",
    error_deleting_stage: "Erreur lors de la suppression de l'étape",
    attached_apps: "Application (optionnel)",
    app_optional_desc: "Sélectionnez une application pour automatiser le filtrage et la notation des candidats à cette étape.",
    no_app: "Aucune automatisation",
    no_app_desc: "Révision manuelle uniquement",
    app_config: "Configuration",
    stage_owner: "Responsable",
    select_owner: "Sélectionner un responsable",
    notify_owner: "Notifier le responsable lorsqu'un candidat arrive à cette étape",
    owner_desc: "La personne responsable de cette étape",
    system: "Système",
    share_title: "Shortlist publique",
    share_desc: "Toute personne ayant le lien voit les candidats actuellement à cette étape. Sans connexion. Nom, poste et évaluation IA uniquement — les coordonnées restent privées.",
    share_on: "Lien public activé",
    share_off: "Partage désactivé",
    share_link: "Lien public",
    share_copy: "Copier le lien",
    share_copied: "Lien copié",
    share_open: "Ouvrir",
    share_failed: "Impossible de mettre à jour le partage",
  },
};

// Custom User Selector Component
function UserSelector({ value, onChange, users, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedUser = users.find((u) => u._id === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white hover:border-gray-400 transition-colors flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      >
        {selectedUser ? (
          <div className="flex items-center gap-3">
            {selectedUser.avatar ? (
              <img src={selectedUser.avatar} alt="" className="w-6 h-6 rounded-full" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                {selectedUser.firstname?.[0]}
                {selectedUser.lastname?.[0]}
              </div>
            )}
            <div className="text-sm font-medium text-gray-900">
              {selectedUser.firstname} {selectedUser.lastname}
            </div>
          </div>
        ) : (
          <span className="text-gray-500 text-sm">{t("select_owner")}</span>
        )}
        <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {/* No owner option */}
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setIsOpen(false);
            }}
            className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center justify-between"
          >
            <span className="text-sm text-gray-600">{t("select_owner")}</span>
            {!value && <FiCheck className="w-4 h-4 text-indigo-600" />}
          </button>

          <div className="border-t border-gray-100" />

          {users.map((user) => {
            const isSelected = value === user._id;
            return (
              <button
                key={user._id}
                type="button"
                onClick={() => {
                  onChange(user);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3 ${isSelected ? "bg-indigo-50" : ""}`}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                    {user.firstname?.[0]}
                    {user.lastname?.[0]}
                  </div>
                )}
                <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                  {user.firstname} {user.lastname}
                </span>
                {isSelected && <FiCheck className="w-4 h-4 text-indigo-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Default stages fallback if job has no custom stages (same as Candidates view)
const DEFAULT_STAGES = [
  { name: "Application", key: "APPLIED", type: "manual", count: 0, color: "indigo" },
  { name: "Selected", key: "SELECTED", type: "manual", count: 0, color: "emerald" },
  { name: "Refused", key: "REFUSED", type: "manual", count: 0, color: "red" },
];

export default function StageConfig({ job, setJob }) {
  const { t, i18n } = useComponentTranslation(translations);
  const { user, organization, setEntity, clearEntity } = useStore();
  const { id, stageId } = useParams(); // Changed stageIndex to stageId
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stage, setStage] = useState(null); // Local state for single stage

  // Default back navigation
  const handleBack = () => {
    navigate(`/recruiter/job-ads/${id}/pipeline`);
  };

  // Fetch users for owner selection (unchanged)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { ok, data } = await api.post("/user/search", {
          organization_id: organization?._id, organization_name: organization?.name,
          limit: 100,
          role_recruiter: true,
        });
        if (ok) setUsers(data);
      } catch (e) {
        console.error("Error fetching users:", e);
      }
    };
    if (organization?._id) fetchUsers();
  }, [organization?._id]);

  // Fetch the SPECIFIC stage by ID
  useEffect(() => {
    const fetchStage = async () => {
      if (!stageId) return;
      try {
        // Option 1: If your API supports getting a stage by ID directly
        // const { ok, data } = await api.get(`/stage/${stageId}`);

        // Option 2 (Fallback): Fetch all stages for job and find the one (since we rely on job context)
        const { ok, data } = await api.post("/stage/search", { job_id: id });

        if (ok && data) {
          const foundStage = data.find((s) => s.key === stageId || s._id === stageId || s.id === stageId);
          if (foundStage) {
            setStage(foundStage);
            setFormData(foundStage);
            setEntity("stage", { name: foundStage.name });
          } else {
            // Handle case where stage ID is valid but not found in list (maybe default stage logic?)
            // For default stages without DB IDs, we might need a fallback map
            const defaultStage = DEFAULT_STAGES.find((s) => s.key === stageId || s.id === stageId);
            if (defaultStage) {
              setStage(defaultStage);
              setFormData(defaultStage);
              setEntity("stage", { name: defaultStage.name });
            } else {
              toast.error("Stage not found");
            }
          }
        }
      } catch (e) {
        console.error("Error fetching stage:", e);
        toast.error("Error loading stage details");
      }
    };
    fetchStage();
    return () => clearEntity("stage");
  }, [id, stageId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // If the stage has a _id, it's a real DB stage -> PUT /stage/:id
      if (formData._id) {
        const response = await api.put(`/stage/${formData._id}`, formData);
        if (response.ok) {
          toast.success(t("save_success"));
          handleBack();
        } else {
          toast.error(response.data.message);
        }
      } else {
        // It's a default/system stage or new stage that hasn't been persisted individually yet?
        // In our new flow, all stages should be real DB objects ideally.
        // But if we are editing a "locked" system stage that is auto-generated but not in DB...
        // We might need to create it or update the job's config override.
        // Assuming we only edit created stages for now.
        toast.error("Cannot edit system default stages directly yet.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating stage");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("delete_confirm"))) {
      return;
    }
    setLoading(true);

    try {
      if (formData._id) {
        const response = await api.remove(`/stage/${formData._id}`);
        if (response.ok) {
          toast.success(t("stage_deleted"));
          handleBack();
        } else {
          toast.error(response.data.message);
        }
      } else {
        toast.error("Cannot delete system stage");
      }
    } catch (e) {
      console.error(e);
      toast.error(t("error_deleting_stage"));
    } finally {
      setLoading(false);
    }
  };

  if (!formData) {
    return <Loader />;
  }

  if (!stage) {
    return <div className="p-6 text-center text-gray-600">Stage not found. Please go back.</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto py-6">
        <div className="grid grid-cols-[1fr_1fr] gap-8 items-start">
        {/* Left column: Name + Owner */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t("stage_name")}
              {["application", "selected", "refused"].includes(formData.key) && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{t("system")}</span>
              )}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">{t("stage_description")}</label>
            <input
              type="text"
              value={formData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder={t("stage_description_placeholder")}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">{t("stage_owner")}</label>
            <UserSelector
              value={formData.user_id}
              onChange={(selectedUser) => {
                setFormData((prev) => ({
                  ...prev,
                  user_id: selectedUser?._id || "",
                  user_name: selectedUser ? `${selectedUser.firstname} ${selectedUser.lastname}` : "",
                  user_avatar: selectedUser?.avatar || "",
                  user_email: selectedUser?.email || "",
                }));
              }}
              users={users}
              t={t}
            />
            {formData.user_id && (
              <label className="flex items-start gap-3 cursor-pointer group p-3 mt-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.notify || false}
                  onChange={(e) => handleChange("notify", e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-900">{t("notify_owner")}</span>
              </label>
            )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-100 pt-6 flex justify-between items-center">
            {!["application", "selected", "refused"].includes(formData.key) ? (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="text-red-600 font-medium hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed px-2"
              >
                {t("delete")}
              </button>
            ) : (
              <div></div>
            )}

            <div className="flex gap-3">
              <button onClick={handleBack} className="px-6 py-2.5 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 border border-gray-300 transition-colors">
                {t("cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : t("save")}
              </button>
            </div>
          </div>
        </div>

        {/* Right column: public sharing */}
        <div>
          {formData._id ? <SharePanel stage={formData} setStage={setFormData} t={t} /> : null}
        </div>
      </div>
      </div>
    </div>
  );
}

function SharePanel({ stage, setStage, t }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/share/stage/${stage._id}`;

  const toggle = async (e) => {
    const next = e.target.checked;
    setBusy(true);
    try {
      const res = await api.put(`/stage/${stage._id}`, { shared: next });
      if (res.ok) {
        setStage((prev) => ({ ...prev, shared: res.data.shared, shared_at: res.data.shared_at }));
      } else {
        toast.error(t("share_failed"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("share_failed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(t("share_copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{t("share_title")}</h3>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t("share_desc")}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0">
          <input type="checkbox" className="sr-only peer" checked={!!stage.shared} onChange={toggle} disabled={busy} />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      </div>

      {stage.shared ? (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={publicUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2 rounded-md border border-gray-200 bg-gray-50 text-xs text-gray-700 font-mono"
            />
            <button
              type="button"
              onClick={copy}
              className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium flex items-center gap-1.5"
              title={t("share_copy")}
            >
              {copied ? <FiCheck className="w-3.5 h-3.5 text-emerald-600" /> : <FiCopy className="w-3.5 h-3.5" />}
              {t("share_copy")}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium flex items-center gap-1.5"
              title={t("share_open")}
            >
              <FiExternalLink className="w-3.5 h-3.5" />
              {t("share_open")}
            </a>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{t("share_on")}</p>
        </div>
      ) : (
        <p className="text-[11px] text-gray-400 mt-3">{t("share_off")}</p>
      )}
    </div>
  );
}
